'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';

const NAV_LINKS = [
  { href: '/career-search', label: 'Career Search' },
  { href: '/resume-builder', label: 'Resume Builder' },
  { href: '/scholarships', label: 'Scholarships' },
  { href: '/job-analyzer', label: 'Job Analyzer' },
  { href: '/video-templates', label: 'Video Templates' },
  { href: '/pricing', label: 'Pricing' },
];

function CareerLiftLogo({ size = 28 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <rect width="32" height="32" rx="8" fill="#2563eb" />
      <path d="M7 21L12 11L17 16L21 10" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
      <circle cx="21" cy="10" r="2.5" fill="white"/>
      <path d="M7 25h18" stroke="rgba(255,255,255,0.4)" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  );
}

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const pathname = usePathname();
  const router = useRouter();
  const { user, loading, signOut } = useAuth();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setMobileOpen(false); }, [pathname]);

  useEffect(() => {
    if (mobileOpen) { document.body.style.overflow = 'hidden'; }
    else { document.body.style.overflow = ''; }
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  const handleSignOut = async () => {
    try { await signOut(); router?.push('/'); router?.refresh(); } catch {}
  };

  // Determine if we're on a dark-bg page (home hero) vs light page
  const isHomePage = pathname === '/' || pathname === '/home';

  return (
    <>
      <header
        className="fixed top-0 left-0 right-0 z-50 transition-all duration-300"
        style={{
          background: scrolled ? 'rgba(255,255,255,0.97)' : isHomePage ? 'transparent' : 'rgba(255,255,255,0.97)',
          borderBottom: scrolled || !isHomePage ? '1px solid rgba(219,234,254,0.8)' : '1px solid transparent',
          backdropFilter: scrolled || !isHomePage ? 'blur(20px)' : 'none',
          boxShadow: scrolled ? '0 1px 20px rgba(59,130,246,0.08)' : 'none',
          paddingTop: 'var(--safe-top)',
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-4">
          {/* Logo */}
          <Link href="/" className="flex items-center gap-2.5 shrink-0 group" aria-label="CareerLift home">
            <CareerLiftLogo size={30} />
            <span className={`text-base font-bold tracking-tight transition-colors ${scrolled || !isHomePage ? 'text-slate-800' : 'text-white'}`}>
              Career<span className="text-blue-600">Lift</span>
            </span>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-1" aria-label="Main navigation">
            {NAV_LINKS.map(link => {
              const active = pathname?.startsWith(link.href);
              return (
                <Link
                  key={link.href}
                  href={link.href}
                  className="flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-medium transition-all duration-200"
                  style={{
                    background: active ? 'rgba(37,99,235,0.1)' : 'transparent',
                    color: active ? '#2563eb' : (scrolled || !isHomePage ? '#475569' : 'rgba(255,255,255,0.85)'),
                    border: active ? '1px solid rgba(37,99,235,0.2)' : '1px solid transparent',
                  }}
                >
                  {link.label}
                </Link>
              );
            })}
          </nav>

          {/* Desktop CTAs */}
          <div className="hidden md:flex items-center gap-2">
            {!loading && (
              <>
                {user ? (
                  <div className="flex items-center gap-2">
                    <Link
                      href="/dashboard"
                      className="px-4 py-2 rounded-lg text-sm font-semibold text-blue-600 hover:bg-blue-50 transition-colors border border-blue-200"
                      style={{ minHeight: '36px', display: 'inline-flex', alignItems: 'center' }}
                    >
                      Dashboard
                    </Link>
                    <Link
                      href="/account-settings"
                      className="px-3 py-2 rounded-lg text-xs font-medium text-slate-500 hover:bg-slate-100 transition-colors"
                      style={{ minHeight: '36px', display: 'inline-flex', alignItems: 'center' }}
                    >
                      Settings
                    </Link>
                    <button
                      onClick={handleSignOut}
                      className="px-3 py-2 rounded-lg text-xs font-medium text-slate-500 hover:bg-slate-100 transition-colors"
                      style={{ minHeight: '36px' }}
                    >
                      Sign Out
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <Link
                      href="/login"
                      className="px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                      style={{
                        color: scrolled || !isHomePage ? '#475569' : 'rgba(255,255,255,0.85)',
                        minHeight: '36px', display: 'inline-flex', alignItems: 'center',
                      }}
                    >
                      Sign In
                    </Link>
                    <Link
                      href="/register"
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold bg-blue-600 text-white hover:bg-blue-700 transition-colors shadow-sm"
                      style={{ minHeight: '36px' }}
                    >
                      Get Started
                    </Link>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Mobile hamburger */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden p-2.5 rounded-lg transition-colors"
            style={{
              color: scrolled || !isHomePage ? '#475569' : 'white',
              border: `1px solid ${scrolled || !isHomePage ? 'rgba(219,234,254,0.8)' : 'rgba(255,255,255,0.25)'}`,
              minWidth: '44px', minHeight: '44px',
            }}
            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileOpen}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              {mobileOpen ? <path d="M18 6L6 18M6 6l12 12" /> : <><line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="18" x2="21" y2="18" /></>}
            </svg>
          </button>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-30 md:hidden bg-black/30 backdrop-blur-sm" onClick={() => setMobileOpen(false)} aria-hidden="true" />
      )}

      {/* Mobile Menu Panel */}
      <div
        className="fixed inset-x-0 top-16 z-40 md:hidden border-b border-blue-100"
        style={{
          background: 'rgba(255,255,255,0.98)',
          backdropFilter: 'blur(24px)',
          transform: mobileOpen ? 'translateY(0)' : 'translateY(-8px)',
          opacity: mobileOpen ? 1 : 0,
          pointerEvents: mobileOpen ? 'auto' : 'none',
          transition: 'transform 0.25s cubic-bezier(0.22,1,0.36,1), opacity 0.2s ease',
          paddingBottom: 'var(--safe-bottom)',
        }}
        aria-hidden={!mobileOpen}
      >
        <nav className="max-w-7xl mx-auto px-4 py-4 space-y-1" aria-label="Mobile navigation">
          {NAV_LINKS.map(link => {
            const active = pathname?.startsWith(link.href);
            return (
              <Link
                key={link.href}
                href={link.href}
                className="flex items-center gap-3 px-4 py-3.5 rounded-xl text-sm font-medium transition-all"
                style={{
                  background: active ? 'rgba(37,99,235,0.08)' : 'rgba(248,250,252,0.8)',
                  color: active ? '#2563eb' : '#334155',
                  border: active ? '1px solid rgba(37,99,235,0.2)' : '1px solid rgba(219,234,254,0.6)',
                  minHeight: '52px',
                }}
              >
                {link.label}
              </Link>
            );
          })}

          <div className="pt-3 space-y-2 border-t border-blue-100">
            {!loading && (
              <>
                {user ? (
                  <>
                    <Link
                      href="/dashboard"
                      className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl font-bold text-sm bg-blue-600 text-white"
                      style={{ minHeight: '52px' }}
                    >
                      Dashboard
                    </Link>
                    <Link
                      href="/account-settings"
                      className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl font-medium text-sm border border-blue-200 text-blue-600"
                      style={{ minHeight: '52px' }}
                    >
                      Account Settings
                    </Link>
                    <button
                      onClick={handleSignOut}
                      className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl font-medium text-sm border border-slate-200 text-slate-600"
                      style={{ minHeight: '52px' }}
                    >
                      Sign Out
                    </button>
                  </>
                ) : (
                  <>
                    <Link
                      href="/register"
                      className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl font-bold text-sm bg-blue-600 text-white"
                      style={{ minHeight: '52px' }}
                    >
                      Get Started Free
                    </Link>
                    <Link
                      href="/login"
                      className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl font-medium text-sm border border-blue-200 text-blue-600"
                      style={{ minHeight: '52px' }}
                    >
                      Sign In
                    </Link>
                  </>
                )}
              </>
            )}
          </div>
        </nav>
      </div>
    </>
  );
}